import React, { useState, useEffect } from 'react';
import Dropdown from '../../components/common/Dropdown';
import { FaEye, FaFilter, FaInfoCircle } from "react-icons/fa";
import axios from 'axios';
import { Link } from "react-router-dom";
import { IoIosCloseCircleOutline } from 'react-icons/io';
import { hostNameBack, today } from '../../utils/constants';
import { CSVLink } from 'react-csv';
import SearchInput from '../../components/common/SearchInput';
import Loader from '../../utils/Loader'; // Adjust the path as necessary

function AdminOrders({ pre }) {
    const [orders, setOrders] = useState([]);
    const [ordersType, setOrdersType] = useState('Products');
    const [filterType, setFilterType] = useState('All');
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [showOrderDetailsModel, setShowOrderDetailsModel] = useState(false);
    const [openedOrder, setOpenedOrder] = useState(null);
    const [openedSubOrder, setOpenedSubOrder] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchType, setSearchType] = useState("ID");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [showFiltersPopup, setShowFiltersPopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tempFilterType, setTempFilterType] = useState(filterType);
    const [tempStartDate, setTempStartDate] = useState(startDate);
    const [tempEndDate, setTempEndDate] = useState(endDate);

    const productFilters = ["All", "Active", "Shipped", "Delivered", "Completed", "On Hold", "Cancelled", "InDispute", "Resolved"];
    const serviceFilters = ["All", "Active", "Delivered", "Completed", "Cancelled", "Past Due", "InDispute", "Resolved"];

    useEffect(() => {
        if (showFiltersPopup) {
            setTempFilterType(filterType);
            setTempStartDate(startDate);
            setTempEndDate(endDate);
        }
    }, [endDate, filterType, showFiltersPopup, startDate]);

    useEffect(() => {
        const fetchOrders = async () => {
            setIsLoading(true);
            const token = localStorage.getItem('adminToken');
            try {
                const response = await axios.get(`${hostNameBack}/api/v1/orders/admin/all/`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { ordersType, pre }
                });
                if (response.data.success) {
                    setOrders(response.data.orders);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Error fetching orders:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchOrders();
    }, [ordersType, pre]);

    useEffect(() => {
        if (filterType === 'All') {
            setFilteredOrders(orders);
        } else {
            const filtered = orders.filter(order =>
                ordersType === 'Products'
                    ? order.products.some(product => product.status[product.status.length - 1].name === filterType)
                    : order.service.status[order.service.status.length - 1].name === filterType
            );
            setFilteredOrders(filtered);
        }
    }, [filterType, orders, ordersType]);

    const getCompletionDate = (statusArray) => {
        const completedStatus = statusArray?.find(status => status.name === "Completed");
        return completedStatus ? new Date(completedStatus.createdAt) : null;
    };

    const finalProductItems = ordersType === "Products"? (filteredOrders || []).flatMap(order =>
            (order?.products || [])
                .filter(item => {
                    const statusArray = item?.status || [];
                    const completionDate = getCompletionDate(statusArray);
                    const query = searchQuery.toLowerCase();

                    if (filterType !== "All" && item?.status[item?.status?.length - 1]?.name !== filterType) return false;

                    if (startDate && endDate && completionDate) {
                        const start = new Date(startDate).getTime();
                        const end = new Date(endDate).getTime();
                        const completionTime = completionDate.getTime();
                        if (completionTime < start || completionTime > end) return false;
                    } else if (startDate && endDate && !completionDate) {
                        return false;
                    }

                    if (searchQuery) {
                        if (searchType === "ID" && !item?.customOrderId?.toLowerCase().includes(query)) return false;
                        if (searchType === "Buyer" && !order?.userId?.username?.toLowerCase().includes(query)) return false;
                        if (searchType === "Seller" && !item?.sellerId?.userId?.username?.toLowerCase().includes(query)) return false;
                    }

                    return true;
                })
                .map(item => ({ order, item }))
        ) : [];

    const finalServiceOrders = ordersType === "Services" ? filteredOrders
        .filter(order => {
            const statusArray = order?.service?.status || [];
            const completionDate = getCompletionDate(statusArray);
            const query = searchQuery.toLowerCase();

            if (startDate && endDate && completionDate) {
                const start = new Date(startDate).getTime();
                const end = new Date(endDate).getTime();
                const completionTime = completionDate.getTime();
                if (completionTime < start || completionTime > end) return false;
            } else if (startDate && endDate && !completionDate) {
                return false;
            }

            if (searchQuery) {
                if (searchType === "ID" && !order?.customOrderId?.toLowerCase().includes(query)) return false;
                if (searchType === "Buyer" && !order?.userId?.username?.toLowerCase().includes(query)) return false;
                if (searchType === "Seller" && !order?.service?.sellerId?.userId?.username?.toLowerCase().includes(query)) return false;
            }

            return true;
        })
        : [];

    const handleOrdersTypeChange = (type) => {
        setOrdersType(type);
        setFilterType("All");
        setStartDate("");
        setEndDate("");
    };

    const handleOpenOrderDetails = (order, subOrder, isProduct) => {
        setOpenedOrder(order);
        setOpenedSubOrder(subOrder);
        setShowOrderDetailsModel(true);
    };

    const productCsvHeaders = [
        { label: "Order ID", key: "orderId" },
        { label: "Product ID", key: "productId" },
        { label: "Product Name", key: "productName" },
        { label: "Quantity", key: "quantity" },
        { label: "Buyer", key: "buyer" },
        { label: "Seller", key: "seller" },
        { label: "Sales Price", key: "salesPrice" },
        { label: "Shipping Fees", key: "shippingFees" },
        { label: "Service Fee", key: "serviceFee" },
        { label: "Subtotal", key: "subtotal" },
        { label: "Tax", key: "tax" },
        { label: "Total Paid by Buyer", key: "totalPaid" },
        { label: "Seller To Get", key: "sellerToGet" },
        { label: "Status", key: "status" },
        { label: "Order Date", key: "orderDate" },
        { label: "Completion Date", key: "completionDate" },
        { label: "Payment Method", key: "paymentMethod" },
        { label: "Coupon Applied", key: "couponApplied" },
        { label: "Buyer Name", key: "buyerName" },
        { label: "Buyer Email", key: "buyerEmail" },
        { label: "Buyer Country", key: "buyerCountry" },
        { label: "Buyer City", key: "buyerCity" },
        { label: "Buyer Phone", key: "buyerPhone" },
    ];

    const serviceCsvHeaders = [
        { label: "Order ID", key: "orderId" },
        { label: "Service ID", key: "serviceId" },
        { label: "Service Name", key: "serviceName" },
        { label: "Buyer", key: "buyer" },
        { label: "Seller", key: "seller" },
        { label: "Sales Price", key: "salesPrice" },
        { label: "Service Fee", key: "serviceFee" },
        { label: "Tax", key: "tax" },
        { label: "Total Paid by Buyer", key: "totalPaid" },
        { label: "Seller To Get", key: "sellerToGet" },
        { label: "Status", key: "status" },
        { label: "Order Date", key: "orderDate" },
        { label: "Completion Date", key: "completionDate" },
        { label: "Payment Method", key: "paymentMethod" },
        { label: "Coupon Applied", key: "couponApplied" },
        { label: "Buyer Name", key: "buyerName" },
        { label: "Buyer Email", key: "buyerEmail" },
        { label: "Buyer Country", key: "buyerCountry" },
        { label: "Buyer City", key: "buyerCity" },
        { label: "Buyer Phone", key: "buyerPhone" },
    ];

    const csvData = ordersType === "Products"? (finalProductItems || []).map(({ order, item } = {}) => ({
        orderId: item?.customOrderId ? `#${item.customOrderId}` : "N/A",
        productId: item?.productId?._id || "N/A",
        productName: item?.productId?.title || "N/A",
        quantity: item?.count || 0,
        buyer: order?.userId?.username || "N/A",
        seller: item?.sellerId?.userId?.username || "N/A",
        salesPrice: item?.buyerPaid?.salesPrice?.toFixed(2) || "0.00",
        shippingFees: item?.buyerPaid?.shippingFees?.toFixed(2) || "0.00",
        serviceFee: item?.buyerPaid?.commission?.toFixed(2) || "0.00",
        subtotal: item?.buyerPaid?.subtotal?.toFixed(2) || "0.00",
        tax: item?.buyerPaid?.tax?.toFixed(2) || "0.00",
        totalPaid: item?.buyerPaid?.total?.toFixed(2) || "0.00",
        sellerToGet: item?.sellerToGet?.total?.toFixed(2) || "0.00",
        status: item?.status[item?.status?.length - 1]?.name || "N/A",
        orderDate: new Date(order?.createdAt).toLocaleDateString() || "N/A",
        completionDate: getCompletionDate(item?.status)?.toLocaleDateString() || "N/A",
        paymentMethod: order?.paymentMethod || "N/A",
        couponApplied: order?.summary?.paidByBuyer?.promoDiscount > 0 ? `${order.summary.paidByBuyer.promoDiscount}%` : "No",
        buyerName: `${order?.billingInfo?.firstName || ""} ${order?.billingInfo?.lastName || ""}`.trim() || "N/A",
        buyerEmail: order?.billingInfo?.email || "N/A",
        buyerCountry: order?.billingInfo?.country || "N/A",
        buyerCity: order?.billingInfo?.city || "N/A",
        buyerPhone: order?.billingInfo?.phoneNumber || "N/A",
    })) : (finalServiceOrders || []).map(order => ({
        orderId: order?.customOrderId ? `#${order.customOrderId}` : "N/A",
        serviceId: order?.service?.serviceId?._id || "N/A",
        serviceName: order?.service?.serviceId?.title || "N/A",
        buyer: order?.userId?.username || "N/A",
        seller: order?.service?.sellerId?.userId?.username || "N/A",
        salesPrice: order?.summary?.paidByBuyer?.salesPrice?.toFixed(2) || "0.00",
        serviceFee: order?.summary?.paidByBuyer?.commission?.toFixed(2) || "0.00",
        tax: order?.summary?.paidByBuyer?.tax?.toFixed(2) || "0.00",
        totalPaid: order?.summary?.paidByBuyer?.total?.toFixed(2) || "0.00",
        sellerToGet: order?.summary?.sellerToGet?.total?.toFixed(2) || "0.00",
        status: order?.service?.status[order?.service?.status?.length - 1]?.name || "N/A",
        orderDate: new Date(order?.createdAt).toLocaleDateString() || "N/A",
        completionDate: getCompletionDate(order?.service?.status)?.toLocaleDateString() || "N/A",
        paymentMethod: order?.paymentMethod || "N/A",
        couponApplied: order?.summary?.paidByBuyer?.promoDiscount > 0 ? `${order.summary.paidByBuyer.promoDiscount}%` : "No",
        buyerName: `${order?.buyerInfo?.firstName || ""} ${order?.buyerInfo?.lastName || ""}`.trim() || "N/A",
        buyerEmail: order?.buyerInfo?.email || "N/A",
        buyerCountry: order?.buyerInfo?.country || "N/A",
        buyerCity: order?.buyerInfo?.city || "N/A",
        buyerPhone: order?.buyerInfo?.phoneNumber || "N/A",
    }));

    const searchFilters = ["Order ID", "Buyer", "Seller"];
    const searchTypeMap = { "Order ID": "ID", "Buyer": "Buyer", "Seller": "Seller" };
    const placeholder = searchType === "ID" ? "Search by Order ID" : searchType === "Buyer" ? "Search by Buyer" : "Search by Seller";

    return (
        <div className='adminOrdersDiv'>
            <div className='adminOrdersContent'>
                <div className="tableDiv">
                    <div className="tableContent">
                        <div className="upper">
                            <h2 className="secondaryHeading">
                                <span>{pre === "dashboard" ? "Orders" : filterType} </span>{pre === "dashboard" ? "Management" : "Orders"}
                            </h2>
                            <div className="upperRight">
                                <SearchInput
                                    searchType={searchType}
                                    setSearchType={setSearchType}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    searchFilters={searchFilters}
                                    searchTypeMap={searchTypeMap}
                                    placeholder={placeholder}
                                />
                                <Dropdown options={["Products", "Services"]} onSelect={handleOrdersTypeChange} selected={ordersType} />
                                <CSVLink
                                    data={csvData}
                                    headers={ordersType === "Products" ? productCsvHeaders : serviceCsvHeaders}
                                    filename={`orders_${ordersType}_${filterType}.csv`}
                                    className="secondaryBtn"
                                >
                                    Generate CSV
                                </CSVLink>
                                <FaFilter className="filterIcon" onClick={() => setShowFiltersPopup(true)} />
                                {pre === "dashboard" && <Link to="/blo-admin/orders" className='secondaryBtn'>View All {">"}</Link>}
                            </div>
                        </div>
                        {(filterType !== "All" || startDate || endDate) && <div className="appliedFilters">
                            {filterType !== "All" && (
                                <div className="filterChip">
                                    Status: {filterType}
                                    <IoIosCloseCircleOutline className="closeIcon" onClick={() => setFilterType("All")} />
                                </div>
                            )}
                            {(startDate || endDate) && (
                                <div className="filterChip">
                                    Date: {startDate || "Any"} to {endDate || "Any"}
                                    <IoIosCloseCircleOutline className="closeIcon" onClick={() => { setStartDate(""); setEndDate(""); }} />
                                </div>
                            )}
                            {(filterType !== "All" || startDate || endDate) && (
                                <button className="clearFiltersBtn" onClick={() => { setFilterType("All"); setStartDate(""); setEndDate(""); }}>
                                    Clear All Filters
                                </button>
                            )}
                        </div>}
                        <div className="header">
                            <p className="title">Order Id</p>
                            <p>Buyer</p>
                            <p>Seller</p>
                            <p>Amount</p>
                            <p>Status</p>
                            <p>Order Date</p>
                            <p>Actions</p>
                        </div>
                        <div className="rows">
                            {isLoading ? (
                                <Loader type="simpleMini" />
                            ) : ordersType === "Products" ? (
                                finalProductItems.length > 0 ? finalProductItems.map(({ order, item }, index) => (
                                    <div key={index}>
                                        <div className="requestRow row">
                                            <div className="titleField field">
                                                <p className="title">#{item?.customOrderId}</p>
                                            </div>
                                            <p className="buyerField field">{order.userId?.username}</p>
                                            <Link to={`/blo-admin/sellers/${item?.sellerId?._id}`} className="sellerField field">{item?.sellerId?.userId?.username + " >"}</Link>
                                            <p className="priceField field">${item?.sellerToGet?.salesPrice}</p>
                                            <p className="statusField field">{item?.status[item?.status?.length - 1].name}</p>
                                            <p className="dateField field">{new Date(order?.createdAt).toLocaleDateString()}</p>
                                            <div className="actionsField field">
                                                <FaEye className="icon" onClick={() => handleOpenOrderDetails(order, item, true)} />
                                            </div>
                                        </div>
                                    </div>
                                )) : <div className="row">Nothing to show here...</div>
                            ) : (
                                finalServiceOrders.length > 0 ? finalServiceOrders.map((item, index) => (
                                    <div key={index}>
                                        <div className="requestRow row">
                                            <div className="titleField field">
                                                <p className="title">#{item?.customOrderId}</p>
                                            </div>
                                            <p className="buyerField field">{item.userId?.username}</p>
                                            <Link to={`/blo-admin/sellers/${item?.service?.sellerId?._id}`} className="sellerField field">{item?.service?.sellerId?.userId?.username + " >"}</Link>
                                            <p className="priceField field">${item?.summary?.paidByBuyer?.salesPrice}</p>
                                            <p className="statusField field">{item?.service?.status[item?.service?.status?.length - 1].name}</p>
                                            <p className="dateField field">{new Date(item?.createdAt).toLocaleDateString()}</p>
                                            <div className="actionsField field">
                                                <FaEye className="icon" onClick={() => handleOpenOrderDetails(item, null, false)} />
                                            </div>
                                        </div>
                                    </div>
                                )) : <div className="row">Nothing to show here...</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showFiltersPopup && (
                <div className="popupDiv filtersPopup">
                    <div className="popupContent">
                        <div className="filtersForm form">
                            <h3 className='secondaryHeading'>Apply <span>Filters</span></h3>
                            <div className="horizontalLine"></div>
                            <div className="infoText">
                                <FaInfoCircle className="icon" />
                                Applying start and end date filters will search for orders completed within the specified date range.
                            </div>
                            <div className="filterRow customDateRange">
                                <div>Start Date:</div>
                                <input type="date" value={tempStartDate} onChange={e => setTempStartDate(e.target.value)} max={today} />
                            </div>
                            <div className="filterRow customDateRange">
                                <div>End Date:</div>
                                <input type="date" value={tempEndDate} onChange={e => setTempEndDate(e.target.value)} max={today} />
                            </div>
                            <div className="filterRow">
                                <div>Status:</div>
                                <Dropdown options={ordersType === "Products" ? productFilters : serviceFilters} selected={tempFilterType} onSelect={setTempFilterType} />
                            </div>
                            <div className="buttonsDiv">
                                <button className="secondaryBtn" onClick={() => setShowFiltersPopup(false)}>Close</button>
                                <button className="primaryBtn" onClick={() => {
                                    setFilterType(tempFilterType);
                                    setStartDate(tempStartDate);
                                    setEndDate(tempEndDate);
                                    setShowFiltersPopup(false);
                                }}>Apply Filters</button>
                            </div>
                        </div>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowFiltersPopup(false)} />
                    </div>
                </div>
            )}

            {showOrderDetailsModel && (
                <div className="popupDiv addNewModelDiv">
                    <div className="popupContent">
                        <div className='form'>
                            <h2 className="secondaryHeading">About <span>Order</span></h2>
                            <div className="rows">
                                <div className="row">
                                    <div>{openedSubOrder ? "Product" : "Service"} Category</div>
                                    <div className="fw600">{openedSubOrder ? openedSubOrder.productId.category : openedOrder.service.serviceId.category}</div>
                                </div>
                                {openedSubOrder && <div className="row">
                                    <div>Quantity</div>
                                    <div className="fw600">{(openedSubOrder.count < 10 && "0") + openedSubOrder.count}</div>
                                </div>}
                            </div>

                            <div className="horizontalLine"></div>

                            <h2 className="secondaryHeading">Paid by <span>Buyer</span></h2>
                            {openedSubOrder && <div className="rows">
                                {Number(openedOrder.summary.paidByBuyer.promoDiscount) > 0 && <div className="row">
                                    <div>Coupon Applied</div>
                                    <div className="fw600">{openedOrder.summary.paidByBuyer.promoDiscount}% Discount</div>
                                </div>}
                                <div className="row">
                                    <div>Payment Method</div>
                                    <div className="fw600">{openedOrder.paymentMethod.charAt(0).toUpperCase() + openedOrder.paymentMethod.slice(1)}</div>
                                </div>
                                <div className="row">
                                    <div>Sales Price</div>
                                    <div className="fw600">${(openedSubOrder.buyerPaid.salesPrice).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Shipping Fees</div>
                                    <div className="fw600">${(openedSubOrder.buyerPaid.shippingFees).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Service Fee</div>
                                    <div className="fw600">${(openedSubOrder.buyerPaid.commission).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>SubTotal</div>
                                    <div className="fw600">${(openedSubOrder.buyerPaid.subtotal).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Tax</div>
                                    <div className="fw600">${(openedSubOrder.buyerPaid.tax).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Buyer Paid</div>
                                    <div className="fw600">${(openedSubOrder.buyerPaid.total).toFixed(2)}</div>
                                </div>
                            </div>}
                            {!openedSubOrder && <div className="rows">
                                {Number(openedOrder.summary.paidByBuyer.promoDiscount) > 0 && <div className="row">
                                    <div>Coupon Applied</div>
                                    <div className="fw600">{openedOrder.summary.paidByBuyer.promoDiscount}% Discount</div>
                                </div>}
                                <div className="row">
                                    <div>Payment Method</div>
                                    <div className="fw600">{openedOrder.paymentMethod.charAt(0).toUpperCase() + openedOrder.paymentMethod.slice(1)}</div>
                                </div>
                                <div className="row">
                                    <div>Sales Price</div>
                                    <div className="fw600">${(openedOrder.summary.paidByBuyer.salesPrice).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Service Fee</div>
                                    <div className="fw600">${(openedOrder.summary.paidByBuyer.commission).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Tax</div>
                                    <div className="fw600">${(openedOrder.summary.paidByBuyer.tax).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Buyer Paid</div>
                                    <div className="fw600">${(openedOrder.summary.paidByBuyer.total).toFixed(2)}</div>
                                </div>
                            </div>}

                            <div className="horizontalLine"></div>

                            <h2 className="secondaryHeading">Seller <span>Earning</span></h2>
                            {openedSubOrder && <div className="rows">
                                <div className="row">
                                    <div>Sales Price</div>
                                    <div className="fw600">${(openedSubOrder.sellerToGet.salesPrice).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Shipping Fees</div>
                                    <div className="fw600">${(openedSubOrder.sellerToGet.shippingFees).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Service Fee</div>
                                    <div className="fw600">-${(openedSubOrder.sellerToGet.commission).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Seller To Get</div>
                                    <div className="fw600">${(openedSubOrder.sellerToGet.total).toFixed(2)}</div>
                                </div>
                            </div>}
                            {!openedSubOrder && <div className="rows">
                                <div className="row">
                                    <div>Sales Price</div>
                                    <div className="fw600">${(openedOrder.summary.sellerToGet.salesPrice).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Service Fee</div>
                                    <div className="fw600">-${(openedOrder.summary.sellerToGet.commission).toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <div>Seller To Get</div>
                                    <div className="fw600">${(openedOrder.summary.sellerToGet.total).toFixed(2)}</div>
                                </div>
                            </div>}

                            <div className="horizontalLine"></div>

                            <h2 className="secondaryHeading">About <span>Buyer</span></h2>
                            {openedSubOrder && <div className='rows'>
                                <div className="row">
                                    <div>Name</div>
                                    <div className="fw600">{openedOrder.billingInfo.firstName + " " + openedOrder.billingInfo.lastName}</div>
                                </div>
                                <div className="row">
                                    <div>Email</div>
                                    <div className="fw600">{openedOrder.billingInfo.email}</div>
                                </div>
                                <div className="row">
                                    <div>Country</div>
                                    <div className="fw600">{openedOrder.billingInfo.country}</div>
                                </div>
                                <div className="row">
                                    <div>City</div>
                                    <div className="fw600">{openedOrder.billingInfo.city}</div>
                                </div>
                                <div className="row">
                                    <div>Phone</div>
                                    <div className="fw600">{openedOrder.billingInfo.phoneNumber}</div>
                                </div>
                            </div>}
                            {!openedSubOrder && <div className='rows'>
                                <div className="row">
                                    <div>Name</div>
                                    <div className="fw600">{openedOrder.buyerInfo.firstName + " " + openedOrder.buyerInfo.lastName}</div>
                                </div>
                                <div className="row">
                                    <div>Email</div>
                                    <div className="fw600">{openedOrder.buyerInfo.email}</div>
                                </div>
                                <div className="row">
                                    <div>Country</div>
                                    <div className="fw600">{openedOrder.buyerInfo.country}</div>
                                </div>
                                <div className="row">
                                    <div>City</div>
                                    <div className="fw600">{openedOrder.buyerInfo.city}</div>
                                </div>
                                <div className="row">
                                    <div>Phone</div>
                                    <div className="fw600">{openedOrder.buyerInfo.phoneNumber}</div>
                                </div>
                            </div>}
                            <div className="contactBuyerBtnDiv">
                                <Link to={`/blo-admin/chats/?p=${openedOrder?.userId?._id}`}>Contact Buyer {" >"}</Link>
                            </div>

                            <div className="buttonsDiv">
                                <button className="secondaryBtn" type="button" onClick={() => setShowOrderDetailsModel(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowOrderDetailsModel(false)} />
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminOrders;